import { useState, useEffect } from 'react';
import { isSelfSchedulePath, ON_PAGE_FORM_ENABLED } from '../utils/selfScheduleConfig';

const EVENT_NAME = 'self_schedule_test';

// '1': redirect consult-form CTAs to /schedule and hide the on-page consult
//      form — no visible form.
// '2': same as '1', PLUS show the on-page self-schedule form (WEBDEV-4187).
// Bare true/'true'/'on' are the values corporate's campaigns used when '1' was
// the only variant — kept equivalent to '1' for any campaign still sending them.
function parseVariant(value) {
    if (value === '2') return '2';
    if (value === true || value === '1' || value === 'true' || value === 'on') return '1';
    return null;
}

/**
 * Self-schedule experiment flag.
 *
 * Allows Optimizely (or any other caller) to toggle the "self schedule test"
 * variant by dispatching a CustomEvent, or by setting window.__selfScheduleTest
 * before React mounts so the hook picks it up on mount.
 *
 * The contract is deliberately identical to corporate's useSelfScheduleVariant
 * — same window flag, same event name, same variant values — so one Optimizely
 * campaign snippet drives corporate, Smooth and the local sites without
 * per-property branching. Only WHEN the window flag is read differs; see
 * "Hydration" below.
 *
 * Returns the raw, page-agnostic value. Most call sites want
 * useSelfScheduleFlags() below, which also checks the current path is one we
 * run the experiment on.
 *
 * The Optimizely snippet is expected to reset the flag and dispatch a falsy
 * detail on SPA navigation away, and re-toggle on return, so the variant
 * follows the page rather than the session.
 *
 * Hydration: the variant always starts as null and window.__selfScheduleTest
 * is read in an effect, NOT in the useState initializer (which is where
 * corporate reads it). Gatsby renders every page at build time with no window,
 * so the static HTML is always the control tree. If Optimizely sets the flag
 * before React mounts — its intended path — an initializer read would make the
 * very first client render differ from that HTML (on-page form vs <Consult>,
 * plain <a> vs <Link> in the header). With no Suspense boundary, React 18
 * recovers from that mismatch by client-rendering the whole page, not just the
 * form. Reading after mount keeps hydration matching, and the variant applies
 * one commit later.
 *
 * The cost is a brief control-to-variant swap on a cold load. The only
 * variant-dependent elements are the consult form (below the fold) and the
 * header button (same classes and label either way, so the swap is invisible),
 * so that is a sub-second below-fold change, not an above-fold layout shift.
 *
 * Example usage from Optimizely campaign JS:
 *   window.__selfScheduleTest = '1'; // or '2'
 *   window.dispatchEvent(new CustomEvent('self_schedule_test', { detail: '1' }));
 *   // on navigate away:
 *   window.__selfScheduleTest = undefined;
 *   window.dispatchEvent(new CustomEvent('self_schedule_test', { detail: null }));
 */
export function useSelfScheduleVariant() {
    // Always null on the first render, server and client alike — see
    // "Hydration" above.
    const [variant, setVariant] = useState(null);

    useEffect(() => {
        if (typeof window === 'undefined') return;

        // Picks up a flag Optimizely set before React mounted.
        setVariant(parseVariant(window.__selfScheduleTest));

        const handler = (event) => setVariant(parseVariant(event.detail));

        window.addEventListener(EVENT_NAME, handler);
        return () => window.removeEventListener(EVENT_NAME, handler);
    }, []);

    return { variant };
}

/**
 * Resolved self-schedule flags for a page the experiment runs on (see
 * SELF_SCHEDULE_PATHS) — the single gate every call site should use, so the
 * variant subscription above is only mounted once per component even where
 * both flags are needed.
 *
 * - `active`: either variant is set, on a self-schedule path, in a metro that
 *   has at least one open store. Gates the
 *   behavior BOTH variants share — suppressing the on-page consult form,
 *   driving the CTA interceptor, and switching the header's FREE CONSULT
 *   button to a direct /schedule link. True even when `onPageFormActive` is
 *   false, so callers still swap the consult form for the hidden stub.
 * - `onPageFormActive`: variant '2' specifically, AND the on-page form's own
 *   kill-switch (ON_PAGE_FORM_ENABLED). Gates showing the on-page form;
 *   variant '1' never sets this.
 *
 * `metroHasOpenStores` keeps the experiment off in metros that can't take
 * bookings yet. Consult.js renders the inquiry-only NotOpen form when
 * currentMetro.openStores is 0, and both flags being false preserves exactly
 * that: the NotOpen form stays, #consult CTAs keep scrolling to it, and the
 * header's "Request More Information" never deep-links into a booking flow.
 * It defaults to false, so a caller that forgets to pass it gets the experiment
 * switched off rather than booking into a closed metro.
 *
 * `pathname` is passed in rather than read from a router hook: corporate uses
 * useLocation from @reach/router, but that is not a dependency of this repo —
 * only Gatsby's internal @gatsbyjs/reach-router fork is present, as a
 * transitive dep. Layout already receives `location` as a prop and threads
 * `pathname` here, which re-evaluates on SPA navigation just the same.
 */
export function useSelfScheduleFlags(pathname, { metroHasOpenStores = false } = {}) {
    const { variant } = useSelfScheduleVariant();
    const eligible = isSelfSchedulePath(pathname) && metroHasOpenStores;

    return {
        active: variant !== null && eligible,
        onPageFormActive: variant === '2' && eligible && ON_PAGE_FORM_ENABLED,
    };
}
